import React, { useState, useRef, useEffect, useCallback } from "react"
import { tv, type VariantProps } from "tailwind-variants"
import { TabsController, useTabsController } from "./useTabsController";

const cx = (...classNames: (string | undefined | null | false)[]) =>
    classNames.filter(Boolean).join(' ');

const tabsVariants = tv({
    base: "w-full",
    variants: {
        variant: {
            default: "border-b border-gray-200",
            pills: "bg-gray-100 p-1 rounded-lg",
        },
    },
    defaultVariants: {
        variant: "default",
    },
})

const tabListVariants = tv({
    base: "flex overflow-x-auto scrollbar-hide scroll-smooth",
    variants: {
        variant: {
            default: "space-x-8",
            pills: "space-x-1",
        },
        fullWidth: {
            true: "w-full",
            false: "",
        },

    },
})

const tabItemVariants = tv({
    base: cx(
        "inline-flex items-center gap-2 px-3 py-2 font-roboto text-base font-normal whitespace-nowrap shrink-0",
        "transition-all duration-200 cursor-pointer",
        "data-[active=true]:font-medium"
    ),
    variants: {
        variant: {
            default: cx(
                "border-b-3 border-transparent hover:text-gray-700",
                "data-[active=true]:border-b-blue-grey data-[active=true]:text-blue-grey",
            ),
            pills: cx(
                "rounded-md hover:bg-gray-200",
                "data-[active=true]:bg-white data-[active=true]:shadow-sm data-[active=true]:text-blue-grey"
            ),
        },
        fullWidth: {
            true: "flex-1 justify-center",
            false: "",
        },

        iconPosition: {
            left: "flex flex-row",
            right: "flex flex-row-reverse",
        },

        disabled: {
            true: "opacity-50 cursor-not-allowed",
        },
    }
})

export interface TabItem {
    id: string | number;
    label: string
    icon?: React.ReactNode | string
    disabled?: boolean
    badge?: string | number
}




interface TabsProps
    extends Omit<React.ComponentPropsWithoutRef<"div">, "onChange">,
    VariantProps<typeof tabsVariants> {
    items: TabItem[];
    defaultValue?: string | number;
    value?: string | number;
    onChange?: (value: string | number | null) => void;
    children?: React.ReactNode;
    controller?: TabsController;
    fullWidth?: boolean;
    iconPosition?: 'left' | 'right';

}

const ScrollChevron = ({ direction, onClick }: { direction: 'left' | 'right'; onClick: () => void }) => (
    <button
        type="button"
        onClick={onClick}
        className={cx(
            "absolute top-0 bottom-0 z-10 flex items-center px-1",
            "bg-gradient-to-r cursor-pointer transition-opacity duration-200",
            direction === 'left'
                ? "left-0 from-white via-white/90 to-transparent pr-3"
                : "right-0 from-transparent via-white/90 to-white pl-3",
        )}
        aria-label={`Scroll ${direction}`}
    >
        <svg
            className="w-5 h-5 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
        >
            {direction === 'left'
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            }
        </svg>
    </button>
);

const SCROLL_AMOUNT = 200;

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
    ({
        className,
        variant = "default",
        items,
        defaultValue,
        value,
        onChange,
        children,
        controller,
        fullWidth = false,
        iconPosition = 'left',
        ...props
    }, forwardedRef) => {
        const internalController = useTabsController({
            defaultValue,
            value,
            onChange,
        })

        const tabsController = controller || internalController
        const scrollRef = useRef<HTMLDivElement>(null);
        const [canScrollLeft, setCanScrollLeft] = useState(false);
        const [canScrollRight, setCanScrollRight] = useState(false);

        const updateScrollState = useCallback(() => {
            const el = scrollRef.current;
            if (!el) return;
            setCanScrollLeft(el.scrollLeft > 0);
            setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
        }, []);

        useEffect(() => {
            const el = scrollRef.current;
            if (!el) return;
            updateScrollState();
            el.addEventListener('scroll', updateScrollState, { passive: true });
            const observer = new ResizeObserver(updateScrollState);
            observer.observe(el);
            return () => {
                el.removeEventListener('scroll', updateScrollState);
                observer.disconnect();
            };
        }, [updateScrollState, items]);

        const scroll = (direction: 'left' | 'right') => {
            const el = scrollRef.current;
            if (!el) return;
            el.scrollBy({ left: direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT });
        };

        return (
            <div
                ref={forwardedRef}
                className={cx(tabsVariants({ variant }), className, "relative")}
                {...props}
            >
                {canScrollLeft && <ScrollChevron direction="left" onClick={() => scroll('left')} />}
                {canScrollRight && <ScrollChevron direction="right" onClick={() => scroll('right')} />}
                <div
                    ref={scrollRef}
                    className={tabListVariants({ variant, fullWidth, })}
                    role="tablist"
                >
                    {items.map((item) => (
                        <button
                            key={item.id}
                            role="tab"
                            type="button"
                            aria-selected={tabsController.activeValue === item.id}
                            data-active={tabsController.activeValue === item.id}
                            disabled={item.disabled}
                            className={tabItemVariants({ variant, fullWidth, disabled: item.disabled, iconPosition })}
                            onClick={() => !item.disabled && tabsController.handleTabChange(item.id)}
                        >
                            {item.icon && (
                                <span className="flex-shrink-0">
                                    {typeof item.icon === 'string' ? (
                                        <img
                                            src={item.icon}
                                            alt=""
                                            className="w-6 h-6 object-contain"
                                        />
                                    ) : (
                                        item.icon
                                    )}
                                </span>
                            )}
                            <span>{item.label}</span>
                            {item.badge && (
                                <span className={cx(
                                    "inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full",
                                    "bg-gray-100 text-gray-600"
                                )}>
                                    {item.badge}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
                {children && (
                    <div className="mt-4">
                        {children}
                    </div>
                )}
            </div>
        )
    },
)

Tabs.displayName = "Tabs"

export { Tabs, tabsVariants, type TabsProps }
